import { useEffect, useMemo, useState } from "react";
import {
  assetLabel,
  detectPlatform,
  fetchReleaseManifest,
  platformNames,
  type Platform,
  type ReleaseManifest,
} from "../lib/release.ts";
import { QQ_CHANNEL_URL } from "../lib/site.ts";

type ReleaseState = {
  manifest: ReleaseManifest | null;
  loading: boolean;
  error: string;
};

function useReleaseManifest(): ReleaseState {
  const [state, setState] = useState<ReleaseState>({
    manifest: null,
    loading: true,
    error: "",
  });

  useEffect(() => {
    const controller = new AbortController();
    fetchReleaseManifest(controller.signal)
      .then((manifest) => {
        setState({ manifest, loading: false, error: "" });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState({
          manifest: null,
          loading: false,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    return () => controller.abort();
  }, []);

  return state;
}

export function Download() {
  const release = useReleaseManifest();
  const [currentPlatform, setCurrentPlatform] = useState<Platform>("windows");
  const platforms = useMemo<Platform[]>(() => ["windows", "macos", "linux"], []);

  useEffect(() => {
    setCurrentPlatform(detectPlatform());
  }, []);

  return (
    <section id="download" className="relative scroll-mt-24 px-6 py-24 sm:py-32">
      <div
        data-reveal=""
        style={{ ["--reveal-delay" as string]: "0ms" }}
        className="mx-auto max-w-3xl text-center"
      >
        <h2 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          下载桌面应用
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
          按平台获取官方发行版安装包。当前系统会优先高亮推荐下载项。
        </p>
      </div>

      <div
        data-reveal=""
        style={{ ["--reveal-delay" as string]: "100ms" }}
        className="mx-auto mt-12 w-full max-w-6xl"
      >
        {release.manifest ? (
          <div className="grid gap-4 md:grid-cols-3">
            {platforms.map((platform) => {
              const assets =
                release.manifest?.assets.filter((asset) => asset.platform === platform) ?? [];
              const recommended = platform === currentPlatform;
              return (
                <div
                  key={platform}
                  className={`rounded-2xl border p-6 transition-colors ${
                    recommended
                      ? "border-white/20 bg-[oklch(0.19_0.005_285_/_0.96)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]"
                      : "border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.92)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]"
                  }`}
                >
                  <div className="mb-5 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-xl font-semibold tracking-tight">
                        {platformNames[platform]}
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {recommended ? "推荐下载" : "桌面版本"}
                      </p>
                    </div>
                    <span className="rounded-full border border-white/10 bg-foreground/[0.04] px-3 py-1 font-mono text-xs text-muted-foreground">
                      {release.manifest?.version}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2.5">
                    {assets.length > 0 ? (
                      assets.map((asset) => (
                        <a
                          key={`${asset.file}-${asset.sha256 || asset.url}`}
                          href={asset.url}
                          className="rounded-xl border border-white/[0.08] bg-foreground/[0.03] px-4 py-3 text-sm font-medium transition-colors hover:border-white/20 hover:bg-foreground/[0.06]"
                        >
                          {assetLabel(asset)}
                        </a>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">暂无该平台安装包</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mx-auto max-w-2xl rounded-2xl border border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.92)] p-8 text-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]">
            <h3 className="text-xl font-semibold tracking-tight">
              {release.loading ? "正在读取下载清单" : "下载清单暂时无法读取"}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {release.loading
                ? "请稍候…"
                : "请稍后刷新页面，或通过 QQ 频道联系支持获取下载方式。"}
            </p>
            {!release.loading && (
              <a
                href={QQ_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground"
              >
                QQ 支持
              </a>
            )}
          </div>
        )}

        <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.72)] p-5 text-sm text-muted-foreground">
          <details>
            <summary className="cursor-pointer font-medium text-foreground/90">
              macOS 提示「已损坏，无法打开」？
            </summary>
            <p className="mt-3 leading-relaxed">
              这是未签名应用常见提示。将应用拖入「应用程序」后，打开终端执行：
            </p>
            <code className="mt-3 block max-w-full overflow-x-auto rounded-lg border border-border/50 bg-background/50 px-3 py-2 font-mono text-xs text-foreground/90">
              xattr -cr &quot;/Applications/Kegel Reminder.app&quot;
            </code>
          </details>
        </div>
      </div>
    </section>
  );
}
